import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { API_BASE_URL } from '@/lib/config'
import { useAuthStore } from '@/stores/useAuthStore'
import type { ConversationFilter, MessageChannel, SMSConversation, SMSMessage } from '@/types/sms'

interface ConversationsResponse {
  conversations: SMSConversation[]
}

interface ConversationDetailResponse {
  messages: SMSMessage[]
  contact_name?: string
}

export function useSMSConversations(
  filter: ConversationFilter = 'all',
  channel?: MessageChannel,
) {
  return useQuery({
    queryKey: ['sms-conversations', filter, channel ?? 'any'],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filter !== 'all') params.filter = filter
      if (channel) params.channel = channel
      const { data } = await api.get<ConversationsResponse>(
        '/api/sms/conversations/',
        { params: Object.keys(params).length ? params : undefined },
      )
      return data.conversations || []
    },
  })
}

/** Détail d'une conversation : tous les messages avec ce numéro, scopés au canal. */
export function useSMSMessages(
  phoneNumber: string | null,
  channel: MessageChannel = 'sms',
) {
  return useQuery({
    queryKey: ['sms-messages', phoneNumber, channel],
    queryFn: async () => {
      if (!phoneNumber) return [] as SMSMessage[]
      const { data } = await api.get<ConversationDetailResponse>(
        `/api/sms/conversations/${encodeURIComponent(phoneNumber)}/`,
        { params: { channel } },
      )
      return data.messages || []
    },
    enabled: !!phoneNumber,
  })
}

export interface SendSMSInput {
  to_number: string
  message: string
  from_number?: string
  media_s3_keys?: string[]
  /** 'whatsapp' route l'envoi via Meta Cloud API ; absent/'sms' = Twilio. */
  channel?: MessageChannel
}

export function useSendSMS() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: SendSMSInput) => {
      const { data } = await api.post('/api/sms/send/', input)
      return data
    },
    onError: (err) => {
      // Un envoi refusé se dit, il ne disparaît pas en silence.
      const data = isAxiosError(err) ? (err.response?.data as { error?: string } | undefined) : undefined
      if (data?.error === 'tollfree_not_verified') {
        toast.error(i18n.t('sms.errors.tollfreeNotVerifiedTitle'), { description: i18n.t('sms.errors.tollfreeNotVerified') })
      } else if (data?.error === 'us_registration_required') {
        toast.error(i18n.t('sms.errors.usRegistrationRequiredTitle'), { description: i18n.t('sms.errors.usRegistrationRequired') })
      } else {
        toast.error(data?.error || i18n.t('sms.errors.sendFailed'))
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['sms-conversations'] })
      queryClient.invalidateQueries({ queryKey: ['sms-messages', vars.to_number] })
    },
  })
}

/**
 * Résultat d'un upload. Le backend décide du traitement selon le type :
 *
 * - `attachment` : image, audio, PDF. Part en pièce jointe MMS via `s3_key`.
 * - `link` : vidéo. N'est JAMAIS jointe au MMS — Twilio plafonne à 5 Mo et
 *   les opérateurs coupent entre 0,6 et 1,5 Mo. Le fichier est hébergé et
 *   `share_url` s'insère dans le texte du message.
 */
export type UploadedSMSMedia =
  | {
      kind: 'attachment'
      s3_key: string
      content_type: string
      filename: string
      url: string
    }
  | {
      kind: 'link'
      share_url: string
      token: string
      content_type: string
      filename: string
      size_bytes: number
      expires_at: string
    }

/**
 * Upload d'un fichier vers S3 via le backend, qui retourne la s3_key + URL signée.
 * On utilise fetch direct (et non axios) pour le FormData multipart.
 *
 * `destination: 'collegue'` (onglet Équipe) : rien ne passe par l'opérateur,
 * le serveur garde un PDF en pièce jointe au lieu de l'envoyer par lien.
 */
export function useUploadSMSMedia(destination?: 'collegue') {
  return useMutation({
    mutationFn: async (file: File): Promise<UploadedSMSMedia> => {
      const token = useAuthStore.getState().accessToken
      const formData = new FormData()
      formData.append('file', file)
      if (destination) formData.append('destination', destination)
      const res = await fetch(`${API_BASE_URL}/api/sms/upload-media/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `upload failed: ${res.status}`)
      }
      const data = await res.json()
      if (!data.success) throw new Error('upload failed')
      if (data.kind === 'link') {
        return {
          kind: 'link',
          share_url: data.share_url,
          token: data.token,
          content_type: data.content_type,
          filename: data.filename,
          size_bytes: data.size_bytes,
          expires_at: data.expires_at,
        }
      }
      return {
        kind: 'attachment',
        s3_key: data.s3_key,
        content_type: data.content_type,
        filename: data.filename,
        url: data.url,
      }
    },
  })
}
